/**
 * In-page photo viewer for snippets/theme-asset-gallery.liquid.
 *
 * The gallery slides are plain links to the asset file, which is the no-JS
 * behaviour we keep: this module upgrades them, and only then does a click open
 * the lightbox instead of a new tab. A native <dialog> carries the modal work -
 * top layer, ::backdrop, Escape, focus trap, focus restored to the trigger - so
 * the only state here is which photo is showing.
 *
 * Markup contract (see the snippet):
 *   [data-mvz-gallery]         the gallery root, holds the triggers
 *   [data-mvz-open="<i>"]      one per gallery slide, the clickable link
 *   mv-lightbox > [data-mvz-dialog]
 *   [data-mvz-track] > .mvz__slide > img[data-src]
 *   [data-mvz-prev] [data-mvz-next] [data-mvz-close] [data-mvz-counter]
 *   [data-mvz-thumb="<i>"]
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class MvLightbox extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLImageElement[]} */
  #images = [];
  /** @type {HTMLElement[]} */
  #thumbs = [];
  /** @type {HTMLElement | null} */
  #trigger = null;
  #index = 0;

  connectedCallback() {
    if (this.dataset.mvzReady) return;

    const dialog = this.querySelector('[data-mvz-dialog]');
    // An engine without showModal keeps the plain-link behaviour untouched.
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;

    const gallery = this.closest('[data-mvz-gallery]');
    const track = this.querySelector('[data-mvz-track]');
    if (!gallery || !(track instanceof HTMLElement)) return;

    this.dataset.mvzReady = 'true';
    this.#dialog = dialog;
    this.#track = track;
    this.#slides = [...this.querySelectorAll('.mvz__slide')];
    this.#images = [...this.querySelectorAll('.mvz__img')].filter((img) => img instanceof HTMLImageElement);
    this.#thumbs = [...this.querySelectorAll('[data-mvz-thumb]')].filter((el) => el instanceof HTMLElement);

    for (const trigger of gallery.querySelectorAll('[data-mvz-open]')) {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('click', (event) => {
        // Leave modified clicks and middle clicks to the browser: the href is
        // still the real file and "open in new tab" should keep working.
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        this.open(Number(trigger.getAttribute('data-mvz-open')) || 0, /** @type {HTMLElement} */ (trigger));
      });
    }

    this.querySelector('[data-mvz-close]')?.addEventListener('click', () => dialog.close());
    this.querySelector('[data-mvz-prev]')?.addEventListener('click', () => this.#go(this.#index - 1));
    this.querySelector('[data-mvz-next]')?.addEventListener('click', () => this.#go(this.#index + 1));

    this.#thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.#go(i));
    });

    // Anything that is not the photo itself or a control is backdrop: the
    // dialog fills the viewport, so ::backdrop never receives the click.
    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('button, a, img, .mvz__thumbs')) return;
      dialog.close();
    });

    // On the document, not the dialog: disabling the arrow you just clicked
    // hands focus back to the body, and a keydown there never reaches the
    // dialog. While the dialog is open it is modal, so nothing else can want
    // these keys. Escape stays the browser's own.
    document.addEventListener('keydown', (event) => {
      if (!dialog.open) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#go(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.#go(event.key === 'Home' ? 0 : this.#slides.length - 1);
      }
    });

    dialog.addEventListener('close', () => {
      unlockScroll(dialog);
      this.#syncGallery(gallery);
      this.#trigger?.focus({ preventScroll: true });
      this.#trigger = null;
    });

    // Keep the counter, the arrows and the thumbs in step with a swipe.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = this.#slides.indexOf(/** @type {HTMLElement} */ (entry.target));
          if (i > -1 && i !== this.#index) {
            this.#index = i;
            // A swipe moves the index without going through #go, so this is
            // where a swiped-to photo and its next neighbour get their src.
            this.#hydrate();
            this.#paint();
          }
        }
      },
      { root: track, threshold: 0.6 }
    );
    for (const slide of this.#slides) observer.observe(slide);
  }

  /**
   * Opens the viewer on one photo.
   * @param {number} index - Zero-based photo index.
   * @param {HTMLElement} [trigger] - Element that gets focus back on close.
   */
  open(index, trigger) {
    const dialog = this.#dialog;
    if (!dialog) return;
    this.#index = this.#clamp(index);
    this.#trigger = trigger ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    this.#hydrate();
    dialog.showModal();
    lockScroll(dialog);
    // showModal paints before layout settles, so place the photo without
    // smoothing and only then let scrolling animate.
    this.#scrollTo(this.#index, 'auto');
    requestAnimationFrame(() => this.#scrollTo(this.#index, 'auto'));
    this.#paint();
    this.#hydrateRest();
  }

  /**
   * @param {number} index
   * @returns {number}
   */
  #clamp(index) {
    return Math.max(0, Math.min(this.#slides.length - 1, index));
  }

  /** @param {number} index */
  #go(index) {
    const next = this.#clamp(index);
    if (next === this.#index && this.#slides.length > 1) {
      // Still re-align: a half-swipe can leave the track between slides.
      this.#scrollTo(next, reducedMotion() ? 'auto' : 'smooth');
      return;
    }
    this.#index = next;
    this.#hydrate();
    this.#paint();
    this.#scrollTo(next, reducedMotion() ? 'auto' : 'smooth');
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollTo(index, behavior) {
    const track = this.#track;
    const slide = this.#slides[index];
    if (!track || !slide) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior });
  }

  /** Loads the visible photo and its neighbours, nothing else. */
  #hydrate() {
    for (let i = this.#index - 1; i <= this.#index + 1; i += 1) this.#load(this.#clamp(i));
  }

  /**
   * Loads the rest while the browser is idle, so a fast swipe never lands on a
   * photo that has not started downloading yet. Only ever after a first open.
   */
  #hydrateRest() {
    const rest = () => {
      for (let i = 0; i < this.#images.length; i += 1) this.#load(i);
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(rest, { timeout: 1500 });
    else setTimeout(rest, 500);
  }

  /** @param {number} i */
  #load(i) {
    const img = this.#images[i];
    const src = img?.dataset.src;
    if (!img || !src) return;
    img.src = src;
    delete img.dataset.src;
  }

  #paint() {
    const total = this.#slides.length;
    const counter = this.querySelector('[data-mvz-counter]');
    if (counter) counter.textContent = `${this.#index + 1} / ${total}`;

    const prev = this.querySelector('[data-mvz-prev]');
    const next = this.querySelector('[data-mvz-next]');
    const close = this.querySelector('[data-mvz-close]');
    const active = document.activeElement;
    if (prev instanceof HTMLButtonElement) prev.disabled = this.#index === 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#index === total - 1;
    // Reaching the first or last photo must not drop focus out of the dialog:
    // hand it to the opposite arrow, or to close when there is none.
    if (active instanceof HTMLButtonElement && active.disabled) {
      const fallback = active === next ? prev : next;
      const target = fallback instanceof HTMLButtonElement && !fallback.disabled ? fallback : close;
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }

    this.#thumbs.forEach((thumb, i) => {
      if (i === this.#index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  /**
   * Leaves the page gallery on the photo the viewer closed on.
   * @param {Element} gallery
   */
  #syncGallery(gallery) {
    const track = gallery.querySelector('[data-mvg-track]');
    const slide = gallery.querySelectorAll('.mvg__slide')[this.#index];
    if (!(track instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'auto' });
  }
}

if (!customElements.get('mv-lightbox')) {
  customElements.define('mv-lightbox', MvLightbox);
}
